import { useMemo } from 'react';
import { FailureFilters } from '@/types/filters';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { TurbineForForecast, WeibullAnalysisResultWithMetadata } from '@/types/forecast';
import { FailureRecord } from '@/types/failure';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';
import { validateSubset, createTurbineMap, applyTurbineFilters } from '@/utils/forecastCalculationHelpers';

interface PopulationAnalysisResult {
  populationSize: number;
  forecastSubsetSize: number;
  turbinesForForecast: TurbineForForecast[];
  validationErrors: string[];
}

interface UsePopulationAnalysisProps {
  turbineData?: { failures: FailureRecord[] } | null;
  sourceAnalysis: WeibullAnalysisResult | null;
  filters: FailureFilters;
}

export const usePopulationAnalysis = ({
  turbineData,
  sourceAnalysis,
  filters
}: UsePopulationAnalysisProps): PopulationAnalysisResult => {
  return useMemo(() => {
    if (!turbineData?.failures || !sourceAnalysis) {
      return {
        populationSize: 0,
        forecastSubsetSize: 0,
        turbinesForForecast: [],
        validationErrors: sourceAnalysis ? ['No turbine data available'] : ['No source analysis loaded']
      };
    }

    const failures = turbineData.failures;
    const errors: string[] = [];

    // Get source analysis filters for subset validation
    const sourceAnalysisWithMetadata = sourceAnalysis as WeibullAnalysisResultWithMetadata;
    const sourceFilters = sourceAnalysisWithMetadata.metadata?.filtersUsed || {};

    // Validate that current filters are subsets of source analysis
    const siteError = validateSubset(filters.sites, sourceFilters.sites, 'Sites');
    const componentError = validateSubset(filters.componentsMajor, sourceFilters.componentsMajor, 'Components');
    const modelError = validateSubset(filters.turbineModels, sourceFilters.turbineModels, 'Turbine Models');

    if (siteError) errors.push(siteError);
    if (componentError) errors.push(componentError);
    if (modelError) errors.push(modelError);

    // Create turbine map
    const turbineMap = createTurbineMap(failures);
    const allTurbines = Array.from(turbineMap.values());

    // STEP 1: Get initial population matching source analysis filters
    // Check if the analysis has stored population metadata (new analyses)
    const storedPopulation = (sourceAnalysisWithMetadata.metadata as { totalPopulation?: number })?.totalPopulation;

    let initialPopulation: TurbineForForecast[];
    let initialPopulationSize: number;

    if (storedPopulation && typeof storedPopulation === 'number') {
      // Use stored population from analysis metadata (most accurate)
      initialPopulationSize = storedPopulation;

      // Still need to get turbine data for forecast calculation
      initialPopulation = applyTurbineFilters(allTurbines, sourceFilters, true);
    } else {
      // Fallback: Calculate from available data
      initialPopulation = applyTurbineFilters(allTurbines, sourceFilters, true);
      initialPopulationSize = initialPopulation.length;
    }

    // STEP 2: Filter initial population by current forecast filters to get subset
    const forecastTurbines = applyTurbineFilters(initialPopulation, filters, true);

    // Validation checks
    if (initialPopulationSize === 0) {
      errors.push('No turbines in initial Weibull analysis population');
    }

    if (forecastTurbines.length === 0) {
      errors.push('No turbines match the current forecast filters');
    }

    if (forecastTurbines.length < FORECAST_CALCULATION_CONSTANTS.MIN_FORECAST_POPULATION && forecastTurbines.length > 0) {
      errors.push(`Forecast subset too small: ${forecastTurbines.length} turbines (minimum ${FORECAST_CALCULATION_CONSTANTS.MIN_FORECAST_POPULATION} recommended)`);
    }

    if (!sourceAnalysis.parameters || !sourceAnalysis.parameters.shape || !sourceAnalysis.parameters.scale) {
      errors.push('Invalid Weibull parameters in source analysis');
    }

    return {
      populationSize: initialPopulationSize,
      forecastSubsetSize: forecastTurbines.length,
      turbinesForForecast: forecastTurbines,
      validationErrors: errors
    };
  }, [turbineData, sourceAnalysis, filters]);
};